<script lang="ts">
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import { getRequest } from '@/Util/utils';

  // Shown once after logging in when the recent riding is too little to plan from (AccountRestartController::loginPrompt):
  // the user can carry on, or restart the account (its old data removed and the account setup wizard run, which can retrieve
  // up to 90 days of history). Restarting needs a premium or trial subscription; without one it says why, with Subscribe.
  // Closed without restarting (Cancel, the X, Escape), it isn't shown again, and a note says Account Settings has the reset.
  // Following Subscribe isn't closing it: it is shown again on the next My Fitness page, to restart once subscribed.
  export let prompt: { days: number, weeks: number, subscription: 'premium' | 'canceled' | 'trial_ended' };

  let open = true;
  let confirming = false;
  let busy = false;
  let error = '';
  $: canRestart = prompt.subscription === 'premium';
  let restarting = false;
  let dismissed = false;
  $: if (!open && !restarting && !dismissed) dismiss();

  function dismiss() {
    dismissed = true;
    fetch('/my-fitness/restart-account/dismiss', { ...getRequest(), method: 'POST' }).catch(e => console.error(e));
    // @ts-ignore
    window.Xert?.accountNotification?.({
      msg: 'You can reset your account under Account Settings at any time to go to the account setup wizard.',
      url: '/account/settings',
      type: 'info',
      icon: 'fa-circle-info',
    });
  }

  async function restart() {
    busy = true;
    error = '';
    try {
      const response = await fetch('/my-fitness/restart-account', { ...getRequest(), method: 'POST' });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.result) throw new Error(data?.msg ?? `HTTP ${response.status}`);
      restarting = true;
      window.location.href = data.redirect ?? '/wizard';
    } catch (e) {
      console.error(e);
      error = e instanceof Error && e.message && !e.message.startsWith('HTTP') ? e.message : 'Your account could not be restarted. Please try again.';
      busy = false;
    }
  }
</script>

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target">
  <Dialog.Content overlayClass="bg-black/30 backdrop-blur-none" class="box-border w-[calc(100vw-1.5rem)] max-w-[30rem] gap-4">
    <Dialog.Header>
      <Dialog.Title>{confirming ? 'Restart your account?' : 'Not enough recent training data'}</Dialog.Title>
    </Dialog.Header>

    {#if !confirming}
      <div class="grid gap-3 text-sm leading-relaxed text-slate-700">
        <p class="m-0">
          {prompt.days === 0 ? 'You have no riding' : `You have only ${prompt.days} day${prompt.days === 1 ? '' : 's'} of riding`} in the last
          {prompt.weeks} weeks, which is too little for Xert to plan your training from.
        </p>
        <p class="m-0">
          Restarting your account clears your old data and runs the account setup wizard, which can retrieve up to 90 days of history
          to analyse.
        </p>
        {#if !canRestart}
          <p class="m-0 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
            Analyzing activities requires a premium or trial subscription.
            {prompt.subscription === 'canceled' ? 'Your premium subscription has been canceled.' : 'Your free trial has ended.'}
            <a href="/my-fitness/restart-account/subscribe" class="font-semibold text-[#2F7076] underline">Subscribe</a> to restart your account.
          </p>
        {/if}
      </div>
      <div class="flex justify-end gap-2">
        <Dialog.Close asChild let:builder>
          <Button builders={[builder]} variant="outline" size="sm">Cancel</Button>
        </Dialog.Close>
        <Button size="sm" disabled={!canRestart} on:click={() => (confirming = true)}>Restart Account</Button>
      </div>
    {:else}
      <div class="grid gap-3 text-sm leading-relaxed text-slate-700">
        <p class="m-0">
          All your activities, training plans and settings will be removed, and the account setup wizard will start. This can't be undone.
        </p>
        {#if error}<p class="m-0 text-red-700">{error}</p>{/if}
      </div>
      <div class="flex justify-end gap-2">
        <Button variant="outline" size="sm" disabled={busy} on:click={() => (confirming = false)}>Back</Button>
        <Button variant="destructive" size="sm" disabled={busy} on:click={restart}>{busy ? 'Restarting…' : 'Restart Account'}</Button>
      </div>
    {/if}
  </Dialog.Content>
</Dialog.Root>
